import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { ArrowLeft, MapPin, Truck, FileText, Building, Package, CreditCard, Database, Edit2, Trash2, Save, X, Plus, Mail, Download, Printer, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';
import ProductSearchInput from '../components/ProductSearchInput';

const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium mt-0.5">{value}</p>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => {
  const validChildren = React.Children.toArray(children).filter(c => c !== null && c !== undefined && c !== false);
  if (validChildren.length === 0) return null;
  return (
    <div className="bg-card rounded-lg border border-border p-5">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
        {Icon && <Icon className="h-4 w-4" />} {title}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{validChildren}</div>
    </div>
  );
};

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [vendorsList, setVendorsList] = useState([]);

  const PO_EDIT_KEY = `webb_po_edit_${id}`;
  const poEditSaved = loadDraft(PO_EDIT_KEY);

  useAutosave(PO_EDIT_KEY, editing ? { editing, editData } : null);

  const loadOrder = async () => {
    try {
      const data = await api.request(`/api/purchase-orders/${id}/detail`);
      setOrder(data);
    } catch (err) {
      console.error('Failed to load PO', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    if (poEditSaved?.editing && poEditSaved?.editData) {
      setEditData(poEditSaved.editData);
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when id changes; loadOrder + saved edit data are intentional one-shots
  }, [id]);

  const startEdit = () => {
    setEditData({
      vendor_name: order.vendor_name || '',
      contact_person: order.contact_person || '',
      vendor_email: order.vendor_email || '',
      vendor_phone: order.vendor_phone || '',
      remarks: order.remarks || '',
      status: order.status || 'draft',
      freight: order.freight || 0,
      payment_terms: order.payment_terms || '',
      sales_rep: order.sales_rep || '',
      line_items: (order.line_items || []).map(item => ({
        variant_name: item.variant_name || '',
        sku: item.sku || '',
        quantity_ordered: item.quantity_ordered || 0,
        quantity_received: item.quantity_received || 0,
        unit_cost: item.unit_cost || 0,
        discount: item.discount || 0,
        total: item.total || 0,
        variant_id: item.variant_id || '',
        description: item.description || '',
        uom: item.uom || '',
      })),
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.request(`/api/purchase-orders/${id}`, {
        method: 'PUT',
        body: JSON.stringify(editData),
      });
      setEditing(false);
      clearDraft(PO_EDIT_KEY);
      loadOrder();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete PO ${order.po_number}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.request(`/api/purchase-orders/${id}`, { method: 'DELETE' });
      navigate('/purchase-orders');
    } catch (err) {
      alert('Failed to delete: ' + err.message);
      setDeleting(false);
    }
  };

  const handleConfirmDraft = async () => {
    if (!window.confirm(`Confirm PO ${order.po_number}? This will change the status from Draft to Ordered.`)) return;
    setConfirming(true);
    try {
      const updated = await api.request(`/api/purchase-orders/${id}/confirm`, { method: 'POST' });
      setOrder(updated);
      alert(`PO ${order.po_number} confirmed as Ordered!`);
    } catch (err) {
      alert('Failed to confirm: ' + err.message);
    } finally {
      setConfirming(false);
    }
  };


  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-orders/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO-${order.po_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF: ' + err.message);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo) { alert('Please enter an email address'); return; }
    setEmailing(true);
    try {
      await api.request(`/api/purchase-orders/${id}/email`, {
        method: 'POST',
        body: JSON.stringify({ email: emailTo, message: emailMsg }),
      });
      alert(`Purchase order emailed to ${emailTo}`);
      setShowEmailModal(false);
      setEmailMsg('');
    } catch (err) {
      alert('Failed to send: ' + err.message);
    } finally {
      setEmailing(false);
    }
  };

  const updateLineItem = (index, field, value) => {
    setEditData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'quantity_ordered' || field === 'unit_cost' || field === 'discount') {
        const qty = parseFloat(items[index].quantity_ordered) || 0;
        const cost = parseFloat(items[index].unit_cost) || 0;
        const disc = parseFloat(items[index].discount) || 0;
        items[index].total = Math.round((qty * cost - disc) * 100) / 100;
      }
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (index) => {
    setEditData(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }));
  };

  const addLineItem = () => {
    setEditData(prev => ({
      ...prev,
      line_items: [{
        variant_name: '', sku: '', quantity_ordered: 1, quantity_received: 0, unit_cost: 0, discount: 0, total: 0, variant_id: '', description: '', uom: '',
      }, ...prev.line_items],
    }));
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  if (!order) return <div className="p-8 text-center text-red-600">Purchase order not found</div>;

  const statusColor = {
    received: 'bg-green-100 text-green-700',
    ordered: 'bg-blue-100 text-blue-700',
    partial: 'bg-yellow-100 text-yellow-700',
    draft: 'bg-gray-100 text-gray-600',
    cancelled: 'bg-red-100 text-red-700',
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  return (
    <div className="space-y-6" data-testid="purchase-order-detail">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-md" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-2xl font-semibold">{order.po_number}</h2>
          <p className="text-muted-foreground">Purchase Order</p>
        </div>
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusColor[order.status] || 'bg-gray-100'}`} data-testid="po-status">
          {order.status}
        </span>
        {!editing && (
          <>
            <button onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="download-pdf-btn">
              <Download className="h-4 w-4" /> PDF
            </button>
            <button onClick={() => navigate(`/purchase-orders/${id}/print`)}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="print-po-btn">
              <Printer className="h-4 w-4" /> Print PO
            </button>
            <button onClick={async () => { 
              setEmailTo(order.vendor_email || ''); 
              setShowEmailModal(true);
              try { const v = await api.getVendors(); setVendorsList(v); } catch (e) { console.warn('Vendor lookup failed:', e); }
            }}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              data-testid="email-po-btn">
              <Mail className="h-4 w-4" /> Email
            </button>
            <button onClick={startEdit}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="edit-po-btn">
              <Edit2 className="h-4 w-4" /> Edit
            </button>
            <button onClick={handleDelete} disabled={deleting}
              className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50"
              data-testid="delete-po-btn">
              <Trash2 className="h-4 w-4" /> {deleting ? 'Deleting...' : 'Delete'}
            </button>
            {order.status === 'draft' && (
              <button onClick={handleConfirmDraft} disabled={confirming}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                data-testid="confirm-draft-po-btn">
                <CheckCircle2 className="h-4 w-4" /> {confirming ? 'Confirming...' : 'Confirm PO'}
              </button>
            )}
          </>
        )}
        {editing && (
          <>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium disabled:opacity-50"
              data-testid="save-po-btn">
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); clearDraft(PO_EDIT_KEY); }}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="cancel-edit-btn">
              <X className="h-4 w-4" /> Cancel
            </button>
          </>
        )}
      </div>

      {/* Draft Warning Banner */}
      {order.status === 'draft' && !editing && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="draft-warning">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">This PO is a Draft — it has NOT been entered into the system.</p>
            <p className="text-xs text-amber-600">Click "Confirm PO" to change the status to Ordered.</p>
          </div>
          <button onClick={handleConfirmDraft} disabled={confirming}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex-shrink-0">
            {confirming ? 'Confirming...' : 'Confirm PO'}
          </button>
        </div>
      )}

      {/* Edit Form */}
      {editing && (
        <div className="bg-card rounded-lg border border-border p-5 space-y-4" data-testid="edit-form">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Edit2 className="h-4 w-4" /> Edit Purchase Order
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Vendor Name</label>
              <input type="text" value={editData.vendor_name}
                onChange={e => setEditData({...editData, vendor_name: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-vendor-name" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Contact Person</label>
              <input type="text" value={editData.contact_person}
                onChange={e => setEditData({...editData, contact_person: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-contact" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Vendor Email</label>
              <input type="text" value={editData.vendor_email}
                onChange={e => setEditData({...editData, vendor_email: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-vendor-email" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Vendor Phone</label>
              <input type="text" value={editData.vendor_phone}
                onChange={e => setEditData({...editData, vendor_phone: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-vendor-phone" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Sales Rep</label>
              <input type="text" value={editData.sales_rep}
                onChange={e => setEditData({...editData, sales_rep: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-sales-rep" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Status</label>
              <select value={editData.status}
                onChange={e => setEditData({...editData, status: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-status">
                <option value="draft">Draft</option>
                <option value="ordered">Ordered</option>
                <option value="partial">Partial</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Freight ($)</label>
              <input type="number" step="0.01" value={editData.freight}
                onChange={e => setEditData({...editData, freight: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-freight" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Payment Terms</label>
              <input type="text" value={editData.payment_terms}
                onChange={e => setEditData({...editData, payment_terms: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-payment-terms" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-xs text-muted-foreground mb-1">Remarks</label>
              <textarea value={editData.remarks}
                onChange={e => setEditData({...editData, remarks: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm h-16" data-testid="edit-remarks" />
            </div>
          </div>

          {/* Editable Line Items */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Line Items</h4>
              <button onClick={addLineItem} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="add-line-item">
                <Plus className="h-3 w-3" /> Add Item
              </button>
            </div>
            <div className="space-y-2">
              {editData.line_items?.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-muted/30 rounded-md p-2" data-testid={`edit-line-${i}`}>
                  <div className="col-span-3">
                    <ProductSearchInput
                      value={item.variant_name}
                      onChange={(val) => updateLineItem(i, 'variant_name', val)}
                      onSelect={(p) => {
                        const items = [...editData.line_items];
                        items[i] = {
                          ...items[i],
                          variant_name: p.name || p.variant_name || '',
                          sku: p.sku || '',
                          unit_cost: p.cost_price || p.unit_price || 0,
                          variant_id: p.id || p.variant_id || '',
                          quantity_ordered: items[i].quantity_ordered || 1,
                          total: Math.round(((items[i].quantity_ordered || 1) * (p.cost_price || p.unit_price || 0)) * 100) / 100,
                        };
                        setEditData({ ...editData, line_items: items });
                      }}
                      placeholder="Search by product name..."
                    />
                  </div>
                  <div className="col-span-2">
                    <input type="text" placeholder="SKU" value={item.sku}
                      onChange={e => updateLineItem(i, 'sku', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm font-mono" />
                  </div>
                  <div className="col-span-1">
                    <input type="number" placeholder="Qty" value={item.quantity_ordered}
                      onChange={e => updateLineItem(i, 'quantity_ordered', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm text-right" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" placeholder="Cost" value={item.unit_cost}
                      onChange={e => updateLineItem(i, 'unit_cost', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm text-right" />
                  </div>
                  <div className="col-span-1">
                    <input type="number" step="0.01" placeholder="Disc" value={item.discount}
                      onChange={e => updateLineItem(i, 'discount', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm text-right" />
                  </div>
                  <div className="col-span-2 text-right text-sm font-medium tabular-nums">
                    ${(item.total || 0).toFixed(2)}
                  </div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => removeLineItem(i)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                      data-testid={`remove-line-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right mt-2 text-sm font-semibold">
              Total: ${editData.line_items?.reduce((s, i) => s + (i.total || 0), 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* View Mode */}
      {!editing && (
        <>
          {/* Vendor Info */}
          <Section title="Vendor" icon={Building}>
            <Field label="Vendor" value={order.vendor_name} />
            <Field label="Contact" value={order.contact_person} />
            <Field label="Email" value={order.vendor_email} />
            <Field label="Phone" value={order.vendor_phone} />
          </Section>

          {/* Order Info */}
          <Section title="Order Information" icon={FileText}>
            <Field label="Order Date" value={fmtDate(order.order_date || order.created_at)} />
            <Field label="Invoiced Date" value={order.invoiced_date} />
            <Field label="Due Date" value={order.due_date} />
            <Field label="Date Paid" value={order.date_paid} />
            <Field label="Requested Ship Date" value={order.requested_ship_date} />
            <Field label="Location" value={order.location} />
            <Field label="Currency" value={order.currency} />
            <Field label="Payment Method" value={order.payment_method} />
            <Field label="Payment Terms" value={order.payment_terms} />
            <Field label="Amount Paid" value={order.amount_paid ? `$${order.amount_paid.toFixed(2)}` : ''} />
            <Field label="Sales Rep" value={order.sales_rep} />
            <Field label="Pricing Scheme" value={order.pricing_scheme} />
            <Field label="Inventory Status" value={order.inventory_status} />
            <Field label="Remarks" value={order.remarks} />
          </Section>

          {/* Billing Address */}
          <Section title="Billing Address" icon={MapPin}>
            <Field label="Address" value={[order.billing_address1, order.billing_address2].filter(Boolean).join(', ')} />
            <Field label="City" value={order.billing_city} />
            <Field label="State" value={order.billing_state} />
            <Field label="Postal Code" value={order.billing_postal_code} />
            <Field label="Country" value={order.billing_country} />
          </Section>

          {/* Shipping */}
          <Section title="Shipping" icon={Truck}>
            <Field label="Address" value={order.shipping_address1} />
            <Field label="City" value={order.shipping_city} />
            <Field label="State" value={order.shipping_state} />
            <Field label="Postal Code" value={order.shipping_postal} />
            <Field label="Country" value={order.shipping_country} />
            <Field label="Carrier" value={order.shipping_carrier} />
            <Field label="Tracking #" value={order.shipping_tracking} />
          </Section>

          {/* Tax Information */}
          <Section title="Tax Information" icon={CreditCard}>
            <Field label="Taxing Scheme" value={order.taxing_scheme} />
            <Field label={order.tax1_name || 'Tax 1'} value={order.tax1_rate ? `${order.tax1_rate}%` : ''} />
            <Field label={order.tax2_name || 'Tax 2'} value={order.tax2_rate ? `${order.tax2_rate}%` : ''} />
            <Field label="Non-Customer Cost" value={order.non_customer_cost ? `$${order.non_customer_cost.toFixed(2)}` : ''} />
          </Section>

          {/* Line Items */}
          <div className="bg-card rounded-lg border border-border">
            <div className="p-5 border-b border-border">
              <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
                <Package className="h-4 w-4" /> Line Items ({order.line_items?.length || 0})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Product</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">SKU</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Qty Ordered</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Qty Received</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Unit Cost</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Discount</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(order.line_items || []).map((item, i) => (
                    <tr key={i} className="hover:bg-muted/30" data-testid={`line-item-${i}`}>
                      <td className="px-4 py-2.5">
                        <p className="text-sm font-medium">{item.variant_name}</p>
                        {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                        {item.uom && <p className="text-xs text-muted-foreground">UOM: {item.uom}</p>}
                      </td>
                      <td className="px-4 py-2.5 text-sm font-mono">{item.sku || '\u2014'}</td>
                      <td className="px-4 py-2.5 text-sm text-right">{item.quantity_ordered}</td>
                      <td className="px-4 py-2.5 text-sm text-right">{item.quantity_received || 0}</td>
                      <td className="px-4 py-2.5 text-sm text-right">${(item.unit_cost || 0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-sm text-right">{item.discount ? `$${item.discount.toFixed(2)}` : '\u2014'}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium">${(item.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-border bg-muted/30">
              <div className="max-w-xs ml-auto space-y-1">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(order.subtotal || order.total || 0).toFixed(2)}</span></div>
                {order.freight > 0 && <div className="flex justify-between text-sm"><span>Freight</span><span>${order.freight.toFixed(2)}</span></div>}
                {order.tax > 0 && <div className="flex justify-between text-sm"><span>Tax</span><span>${order.tax.toFixed(2)}</span></div>}
                <div className="flex justify-between text-base font-semibold pt-1 border-t border-border">
                  <span>Total</span><span>${(order.total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Raw Import Data */}
          {order.raw_data && Object.keys(order.raw_data).length > 0 && (
            <div className="bg-card rounded-lg border border-border p-5">
              <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider mb-4">
                <Database className="h-4 w-4" /> Additional Import Data
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                {Object.entries(order.raw_data)
                  .filter(([, v]) => v && String(v).trim())
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between py-1 border-b border-muted/50">
                      <span className="text-muted-foreground">{key}</span>
                      <span className="font-medium text-foreground text-right ml-2 truncate">{String(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="email-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Mail className="h-5 w-5" /> Email Purchase Order</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Select Vendor</label>
                <select onChange={e => {
                  const v = vendorsList.find(vendor => vendor.id === e.target.value);
                  if (v && v.email) setEmailTo(v.email);
                }} className="w-full px-3 py-2 border border-input rounded-md text-sm mb-2" data-testid="vendor-email-select">
                  <option value="">-- Choose vendor to auto-fill email --</option>
                  {vendorsList.filter(v => v.email).map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({v.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Email (Vendor) *</label>
                <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                  placeholder="vendor@email.com"
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="email-to-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Additional Message (optional)</label>
                <textarea value={emailMsg} onChange={e => setEmailMsg(e.target.value)}
                  placeholder="Add a note to the email..."
                  className="w-full px-3 py-2 border border-input rounded-md text-sm h-20" data-testid="email-msg-input" />
              </div>
              <p className="text-xs text-muted-foreground">PDF for {order.po_number} (${(order.total || 0).toFixed(2)}) will be attached.</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 border rounded-md text-sm hover:bg-muted">Cancel</button>
              <button onClick={handleSendEmail} disabled={emailing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                data-testid="send-email-btn">
                {emailing ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetail;
